/**
 * Lessons API — migrated from legacy LessonsAPI.js + TasksAPI.js
 * Lessons = containers per group. Tasks (called "задания" in UI) = modules inside a lesson.
 */
import apiClient from './client';
import NotificationsAPI from './notifications.api';
import SlidesAPI, { invalidateSlidesCache } from './slides.api';

/* ── Types ─────────────────────────────────────────────────── */

export interface Lesson {
  id: string;
  name?: string;
  classId?: string;
  startTimestamp?: number;
  summary?: string;
  duration?: number;
  lessonType?: string;
  groupId?: string;
  offlineStudentName?: string;
  isOfflineExternal?: boolean;
  recurringGroupId?: string;
  scheduleTimestamps?: number[];
  status?: string;
  cancelReason?: string;
  notes?: string;
  /** Returned by MongoAPI.transformMongoObj as ms timestamp */
  createdTimestamp?: number;
  /** Same as createdTimestamp, also from transformMongoObj */
  timestamp?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface LessonTask {
  id: string;
  lessonId?: string;
  name?: string;
  /** Attached items (exercises, tests, etc.) */
  items?: LessonTaskItem[];
  deadline?: number | null;
  /** When true, students can restart exercises after completing them */
  allowRetry?: boolean;
  /** Structured content blocks (same format as slides) */
  contentBlocks?: unknown[];
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface LessonTaskItem {
  id: string;
  /** exercise types OR content block types returned by legacy API */
  type: 'exercise' | 'test' | 'card' | 'treadmill' | 'dialog' | 'questionnaire'
      | 'text' | 'video' | 'image' | 'audio' | string;
  /** ID of the referenced slide/module/exercise */
  itemId?: string;
  name?: string;
  [key: string]: unknown;
}

/* ── Lessons ────────────────────────────────────────────────── */

const LessonsAPI = {
  /** Get all teacher lessons across groups + offline */
  async getMyLessons(): Promise<Lesson[]> {
    const { data } = await apiClient.get<Lesson[]>('/v3/my-lessons');
    return (data as any)?.result ?? data ?? [];
  },

  /** All lessons for a group */
  async getGroupLessons(groupId: string): Promise<Lesson[]> {
    const { data } = await apiClient.get<Lesson[]>(
      `/v3/class/${groupId}/lessons`,
    );
    return (data as any)?.result ?? data ?? [];
  },

  /** Single lesson by id */
  async getLesson(id: string): Promise<Lesson> {
    const { data } = await apiClient.get<Lesson>(`/v3/lessons/${id}`);
    return (data as any)?.result ?? data;
  },

  /** Create lesson inside a group */
  async createLesson(
    groupId: string,
    payload: Partial<Lesson>,
  ): Promise<Lesson> {
    const { data: raw } = await apiClient.post<any>(
      `/v3/class/${groupId}/lessons`,
      payload,
    );
    const data = raw?.result ?? raw;
    if (data?.id && groupId && groupId !== 'offline') {
      const dateStr = payload.startTimestamp
        ? new Date(payload.startTimestamp).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
        : '';
      void NotificationsAPI.notify(
        'group:' + groupId,
        '📅 Новое занятие',
        `Запланировано занятие «${payload.name || 'Урок'}» ${dateStr}`,
        { type: 'lesson', groupId, url: `/groups/${groupId}` }
      );
    }
    return data;
  },

  /** Create multiple lessons (batch) for recurring schedule */
  async createBatchLessons(
    groupId: string,
    lessons: Partial<Lesson>[],
  ): Promise<Lesson[]> {
    const { data: raw } = await apiClient.post<any>(
      `/v3/class/${groupId}/lessons/batch`,
      { lessons },
    );
    return raw?.result ?? raw ?? [];
  },

  /** Update lesson */
  async updateLesson(payload: Partial<Lesson> & { id: string }): Promise<Lesson> {
    const { data: raw } = await apiClient.put<any>(
      `/v3/lessons/${payload.id}`,
      payload,
    );
    return raw?.result ?? raw;
  },

  /** Update recurring series of lessons */
  async updateSeries(
    seriesId: string,
    payload: Record<string, any> & { fromTimestamp?: number; timeOffsetMs?: number },
  ): Promise<void> {
    await apiClient.put(`/v3/lessons/series/${seriesId}`, payload);
  },

  /** Delete lesson — endpoint: DELETE /v3/lessons/:id */
  async deleteLesson(id: string): Promise<void> {
    await apiClient.delete(`/v3/lessons/${id}`);
  },

  /** Delete entire recurring series of lessons */
  async deleteSeries(seriesId: string, fromTimestamp?: number): Promise<void> {
    await apiClient.delete(`/v3/lessons/series/${seriesId}`, {
      params: fromTimestamp ? { fromTimestamp } : undefined,
    });
  },
};

/* ── Tasks (zadaniya) ───────────────────────────────────────── */

export const TasksAPI = {
  /** All tasks for a lesson */
  async getLessonTasks(lessonId: string): Promise<LessonTask[]> {
    try {
      const { data } = await apiClient.get<LessonTask[]>(
        `/v3/lesson/${lessonId}/tasks`,
      );
      const list = (data ?? []) as LessonTask[];
      list.forEach(t => SlidesAPI.seedFromTask(t));
      return list;
    } catch (err: any) {
      if (err?.response?.status === 404) return [];
      throw err;
    }
  },

  /** Single task */
  async getTask(id: string): Promise<LessonTask> {
    const { data: raw } = await apiClient.get<any>(`/v3/tasks/${id}`);
    const data = (raw as any)?.result ?? raw;
    if (data?.id) SlidesAPI.seedFromTask(data);
    return data;
  },

  /** Create task inside a lesson */
  async createTask(
    lessonId: string,
    payload: { name?: string; deadline?: number | null },
  ): Promise<LessonTask> {
    const { data: raw } = await apiClient.post<any>(
      `/v3/lesson/${lessonId}/tasks`,
      payload,
    );
    const data = (raw as any)?.result ?? raw;
    if (data?.id) SlidesAPI.seedFromTask(data);
    invalidateSlidesCache();
    return data;
  },

  /** Update task */
  async updateTask(payload: Partial<LessonTask> & { id: string }): Promise<LessonTask> {
    const { data: raw } = await apiClient.put<any>(
      `/v3/tasks/${payload.id}`,
      payload,
    );
    const data = (raw as any)?.result ?? raw;
    if (data?.id) SlidesAPI.seedFromTask(data);
    invalidateSlidesCache();
    return data;
  },

  /** Delete task */
  async deleteTask(id: string): Promise<void> {
    await apiClient.delete(`/v3/tasks/${id}`);
    invalidateSlidesCache();
  },
};

export default LessonsAPI;
